// 🔧 passport_stop.js
// ✅ Purpose: Public Passport Stop detail page for attendees
// ✅ Features:
// - Shows one Passport Stop from engagement_activities
// - Requires event slug and valid activityId route param
// - Shows stop type, reward details, and completion status
// - Allows attendee to redeem a public/manual code for this stop
// - Calls redeemEngagementCode Cloud Function
// - Reloads user Passport summary and completion state after redemption
// - Uses standard LGV success/error notifications for 20 seconds
// - Fully block-commented for clarity and future devs

import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { Link, useParams } from "react-router-dom";

import { auth, db, functions } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import { showStatus } from "../../utils/status_helpers";
import { recordEngagement } from "../../services/engagement/engagement_tracker";
import { ENGAGEMENT_EVENTS } from "../../services/engagement/engagement_event_types";
import QrScannerModal from "../../components/scanner/qr_scanner_modal";
import { parseScanPayload } from "../../services/scanner/scan_payload_parser";
import { SCAN_TYPES } from "../../services/scanner/scan_types";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";

const STOP_TYPES = {
  sponsor: {
    icon: "🤝",
    label: "Sponsor Stop",
  },

  vendor: {
    icon: "🛍️",
    label: "Vendor Stop",
  },

  event: {
    icon: "🎪",
    label: "Event Stop",
  },

  bonus: {
    icon: "🎁",
    label: "Bonus Stop",
  },

  hidden: {
    icon: "🗺️",
    label: "Hidden Stop",
  },

  challenge: {
    icon: "🏆",
    label: "Challenge Stop",
  },

  photo: {
    icon: "📸",
    label: "Photo Stop",
  },

  volunteer: {
    icon: "🙋",
    label: "Volunteer Stop",
  },

  education: {
    icon: "🎓",
    label: "Learning Stop",
  },

  default: {
    icon: "📍",
    label: "Passport Stop",
  },
};

const getStopType = (activity = {}) => {
  const safeActivity = activity || {};
  const stopTypeKey = safeActivity.stopType || "default";

  return STOP_TYPES[stopTypeKey] || STOP_TYPES.default;
};

const getSourceLabel = (activity = {}) => {
  if (activity.sourceModule === "sponsors") {
    return "Sponsor";
  }

  if (activity.sourceModule === "vendors") {
    return "Vendor";
  }

  if (activity.sourceModule === "volunteers") {
    return "Volunteer";
  }

  if (activity.sourceModule === "auction") {
    return "Auction";
  }

  if (activity.sourceModule === "voting") {
    return "Voting";
  }

  if (activity.sourceModule === "store") {
    return "Store";
  }

  if (activity.sourceModule === "tickets") {
    return "Tickets";
  }

  return "Event";
};

const normalizeCode = (value = "") => {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
};

const PassportStop = () => {
  const { activityId } = useParams();

  const {
    eventId,
    eventslug,
    ready,
    eventLoading,
  } = useEventContext({
    redirectIfNotFound: true,
  });

  const [activity, setActivity] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [code, setCode] = useState("");
  const [status, setStatus] = useState(null);
  const [loadingStop, setLoadingStop] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [stopViewRecorded, setStopViewRecorded] = useState(false);
  // ✅ Add here
  const [showScanner, setShowScanner] = useState(false);

  useEffect(() => {
    if (ready && eventId && activity?.id) trackEvent({ action: GA_EVENTS.PASSPORT_STOP_VIEWED, parameters: { event_slug: eventslug, event_id: eventId, source_page: window.location.pathname } });
  }, [ready, eventId, eventslug, activity?.id]);

    const stopType = useMemo(() => {
    return getStopType(activity);
    }, [activity]);

  const completed = useMemo(() => {
    return transactions.some((transaction) => {
      return transaction.activityId === activityId;
    });
  }, [transactions, activityId]);

  useEffect(() => {
    if (!status?.message) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  const loadStop = async () => {
    if (!eventId || !activityId) return;

    try {
      setLoadingStop(true);

      const activityRef = doc(
        db,
        "events",
        eventId,
        "engagement_activities",
        activityId
      );

      const activitySnap = await getDoc(activityRef);

      if (!activitySnap.exists()) {
        setActivity(null);
        return;
      }

      setActivity({
        id: activitySnap.id,
        ...activitySnap.data(),
      });
    } catch (err) {
      logger.error("❌ Failed to load Passport stop:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Failed to load Passport stop.",
      });
    } finally {
      setLoadingStop(false);
    }
  };

  const loadTransactions = async () => {
    if (!eventId || !auth.currentUser?.uid) {
      setTransactions([]);
      return;
    }

    try {
      const transactionsRef = collection(
        db,
        "events",
        eventId,
        "engagement_transactions"
      );

      const snap = await getDocs(
        query(
          transactionsRef,
          where("userId", "==", auth.currentUser.uid),
          where("activityId", "==", activityId)
        )
      );

      setTransactions(
        snap.docs.map((transactionDoc) => ({
          id: transactionDoc.id,
          ...transactionDoc.data(),
        }))
      );
    } catch (err) {
      logger.error("❌ Failed to load Passport stop transactions:", {
        value: err,
      });
    }
  };

  useEffect(() => {
    if (
      !ready ||
      eventLoading ||
      !eventId ||
      !activity ||
      stopViewRecorded
    ) {
      return;
    }

    const recordStopView = async () => {
      try {
        await recordEngagement({
          eventType: ENGAGEMENT_EVENTS.PASSPORT_STOP_VIEWED,
          eventId,
          uid: auth.currentUser?.uid || null,
          source: "passport",
          metadata: {
            page: "passport_stop",
            path: window.location.pathname,
            activityId: activity.id,
            activityTitle: activity.title || "",
            activityType: activity.activityType || "passport_activity",
            sourceModule: activity.sourceModule || "passport",
            sourceType: activity.sourceType || "",
            sourceId: activity.sourceId || "",
          },
        });

        setStopViewRecorded(true);
      } catch (err) {
        logger.warn("⚠ Failed to record Passport stop view:", {
          value: err,
        });
      }
    };

    recordStopView();
  }, [
    ready,
    eventLoading,
    eventId,
    activity,
    stopViewRecorded,
  ]);

  useEffect(() => {
    if (!ready || eventLoading) return;

    loadStop();
    loadTransactions();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, eventLoading, eventId, activityId]);

  const handleRedeemCode = async (e) => {
    e.preventDefault();

    const normalizedCode = normalizeCode(code);

    if (!normalizedCode) {
      showStatus(setStatus, {
        type: "error",
        message: "Please enter this stop's Passport code.",
      });

      return;
    }

    if (!auth.currentUser) {
      showStatus(setStatus, {
        type: "error",
        message: "Please sign in before collecting Passport stops.",
      });

      return;
    }

    try {
      setRedeeming(true);

      const redeemEngagementCode = httpsCallable(
        functions,
        "redeemEngagementCode"
      );

      const result = await redeemEngagementCode({
        eventId,
        code: normalizedCode,
        validationMethod: "manual_code",
      });

      const data = result?.data || {};

      trackEvent({
        action: GA_EVENTS.PASSPORT_STOP_REDEEMED,
        parameters: {
          event_slug: eventslug,
          event_id: eventId,
          activity_id: activityId,
          validation_method: "manual_code",
          points_awarded: Number(data?.pointsAwarded || 0),
          source_page: window.location.pathname,
        },
      });

      trackEvent({
        action: GA_EVENTS.PASSPORT_CODE_REDEEMED,
        parameters: {
          event_slug: eventslug,
          event_id: eventId,
          activity_id: activityId,
          validation_method: "manual_code",
          points_awarded: Number(data?.pointsAwarded || 0),
          source_page: window.location.pathname,
        },
      });

      showStatus(setStatus, {
        type: "success",
        message:
          data?.message ||
          `Stop collected. You earned ${data.pointsAwarded || 0} points.`,
      });

      setCode("");

      await loadTransactions();
    } catch (err) {
      const codeNotFound =
        err?.code === "functions/not-found" || err?.code === "not-found";
      const log = codeNotFound ? logger.warn : logger.error;

      log("❌ Failed to redeem Passport stop code:", {
        value: {
          code: err?.code || null,
          message: err?.message || null,
          details: err?.details || null,
        },
      });

      showStatus(setStatus, {
        type: "error",
        message:
          err?.message ||
          "That code could not be redeemed. Please check the code and try again.",
      });
    } finally {
      setRedeeming(false);
    }
  };

  const handleRedeemScannedCode = async (scannedValue) => {
    const parsedScan = parseScanPayload(scannedValue);

    if (!parsedScan.valid || parsedScan.type !== SCAN_TYPES.PASSPORT) {
      showStatus(setStatus, {
        type: "error",
        message: "This QR code is not a Passport Stop code.",
      });

      return;
    }

    const normalizedCode = normalizeCode(parsedScan.value);

    if (!normalizedCode) return;

    try {
      setRedeeming(true);

      const redeemEngagementCode = httpsCallable(
        functions,
        "redeemEngagementCode"
      );

      const result = await redeemEngagementCode({
        eventId,
        code: normalizedCode,
        validationMethod: "qr",
      });

      const data = result?.data || {};

            trackEvent({
        action: GA_EVENTS.PASSPORT_QR_SCANNED,
        parameters: {
          event_slug: eventslug,
          event_id: eventId,
          activity_id: activityId,
          validation_method: "qr",
          source_page: window.location.pathname,
        },
      });

      trackEvent({
        action: GA_EVENTS.PASSPORT_STOP_REDEEMED,
        parameters: {
          event_slug: eventslug,
          event_id: eventId,
          activity_id: activityId,
          validation_method: "qr",
          points_awarded: Number(data?.pointsAwarded || 0),
          source_page: window.location.pathname,
        },
      });
      
      showStatus(setStatus, {
        type: "success",
        message:
          data?.message ||
          `Stop collected. You earned ${data.pointsAwarded || 0} points.`,
      });

      await loadTransactions();
    } catch (err) {
      logger.error("❌ Failed to redeem scanned Passport stop:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message:
          err?.message ||
          "Unable to redeem QR code.",
      });
    } finally {
      setRedeeming(false);
    }
  };

  if (!ready || eventLoading || loadingStop) {
    return <FullPageSpinner message="Loading Passport Stop..." />;
  }

  if (!activity) {
    return (
      <section id="top-content" className="container-fluid py-5">
        <div className="container" style={{ maxWidth: "900px" }}>
          <div className="alert alert-warning">
            Passport stop not found.
          </div>

          <Link
            to={`/${eventslug}/passport`}
            className="btn btn-outline-secondary"
          >
            Back to Passport
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "900px" }}>
        <div className="mb-4">
          <Link
            to={`/${eventslug}/passport`}
            className="btn btn-outline-secondary btn-sm"
          >
            ← Back to Passport
          </Link>
        </div>

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        {completed && (
          <div className="alert alert-success shadow-sm">
            <strong>✔ Stop Collected!</strong>{" "}
            This Passport Stop has already been completed.
          </div>
        )}

        <div className="card shadow-sm mb-4">
          <div className="card-body p-4">
            <div className="d-flex justify-content-between align-items-start gap-3">
              <div>
                <div className="text-muted small mb-2">
                  {stopType.icon} {stopType.label}
                </div>

                <h1 className="fw-bold mb-2">
                  {activity.title}
                </h1>

                {activity.description && (
                  <p className="lead text-muted mb-0">
                    {activity.description}
                  </p>
                )}
              </div>

              <span
                className={`badge ${
                  completed ? "bg-success" : "bg-primary"
                }`}
              >
                {completed ? "Collected" : "Open"}
              </span>
            </div>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-12 col-lg-5">

            <div className="card shadow-sm mb-4">
            <div className="card-body">
                <h5 className="fw-bold mb-2">
                Stop Details
                </h5>

                <div className="row g-3">
                <div className="col-12 col-md-4">
                    <div className="border rounded p-3 h-100">
                    <div className="text-muted small">
                        Stop Source
                    </div>

                    <div className="fw-bold">
                        {getSourceLabel(activity)}
                    </div>
                    </div>
                </div>

                <div className="col-12 col-md-4">
                    <div className="border rounded p-3 h-100">
                    <div className="text-muted small">
                        Stop Type
                    </div>

                    <div className="fw-bold">
                        {stopType.icon} {stopType.label}
                    </div>
                    </div>
                </div>

                <div className="col-12 col-md-4">
                    <div className="border rounded p-3 h-100">
                    <div className="text-muted small">
                        Status
                    </div>

                    <div className="fw-bold">
                        {completed ? "Collected" : "Available"}
                    </div>
                    </div>
                </div>
                </div>
            </div>
            </div>

            <div className="card shadow-sm h-100">
              <div className="card-header bg-white">
                <strong>Collect This Stop</strong>
              </div>

              <div className="card-body">
                <button
                  type="button"
                  className="btn btn-success w-100 mb-3"
                  disabled={completed || redeeming}
                  onClick={() => {
                    trackEvent({
                      action: GA_EVENTS.PASSPORT_SCANNER_OPENED,
                      parameters: {
                        event_slug: eventslug,
                        event_id: eventId,
                        activity_id: activityId,
                        source: "passport_stop",
                        source_page: window.location.pathname,
                      },
                    });

                    setShowScanner(true);
                  }}
                  >
                  📷 Scan This Stop
                </button>

                <div className="text-center text-muted small mb-3">
                  Can't scan? Enter the code manually below.
                </div>
                <form onSubmit={handleRedeemCode}>
                  <label className="form-label fw-bold">
                    Passport Code
                  </label>

                  <input
                    className="form-control form-control-lg text-uppercase"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="SPONSOR25"
                    autoComplete="off"
                    disabled={completed || redeeming}
                  />

                  <div className="form-text">
                    Enter the code shown at this stop.
                  </div>

                  <button
                    type="submit"
                    className="btn btn-success w-100 mt-4"
                    disabled={completed || redeeming}
                  >
                    {completed
                      ? "Stop Already Collected"
                      : redeeming
                      ? "Collecting..."
                      : "Collect Stop"}
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-7">
            <div className="card shadow-sm h-100">
              <div className="card-header bg-white">
                <strong>Rewards</strong>
              </div>

              <div className="card-body">
                <div className="row g-3 text-center">
                  <div className="col-6">
                    <div className="border rounded p-3">
                      <div className="display-6 fw-bold">
                        +{activity.points || 0}
                      </div>

                      <div className="small text-muted">
                        Points
                      </div>
                    </div>
                  </div>

                  <div className="col-6">
                    <div className="border rounded p-3">
                      <div className="display-6 fw-bold">
                        +{activity.raffleEntries || 0}
                      </div>

                      <div className="small text-muted">
                        Prize Entries
                      </div>
                    </div>
                  </div>
                </div>

                {activity.bonusCodeEnabled && (
                  <div className="alert alert-info mt-4 mb-0">
                    This stop may also have a bonus code for extra rewards.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="alert alert-light border mt-4 mb-0">
          <strong>Tip:</strong> Passport Stops help you explore the event,
          discover sponsors and vendors, complete challenges, and earn rewards.
        </div>
      </div>

      <QrScannerModal
        show={showScanner}
        title="Scan Passport Stop"
        helpText="Align this stop's QR code inside the frame."
        onClose={() => setShowScanner(false)}
        onScan={(value) => {
          setShowScanner(false);
          handleRedeemScannedCode(value);
        }}
      />      
    </section>
  );
};

export default PassportStop;
